<template>
  <div class="shipments-page">
    <div class="vx-row">
      <div class="vx-col w-full lg:w-1/2 mb-base">
        <h2>{{$t('Search')}}</h2>
      </div>
    </div>
    <div class="clearfix w-full">
      <export-button v-if="$store.state.AppActiveUser.userRole !== 'finance'" class="mr-4 float-left whitespace-nowrap"></export-button>
      <print-label-button :selected="selected" class="mr-4 float-left whitespace-nowrap"></print-label-button>
    </div>
    <div class="mb-base">
      <vs-input icon-pack="feather" icon="icon-search" :placeholder="$t('Enter tracking number or phone number')" 
        v-model="searchVal" class="w-full is-label-placeholder" 
        @keyup.enter.native="[searchVal? handleSearch(searchVal): '']"/>
    </div>
    <shipblu-table
      class="mb-base"
      :sst="true"
      :data="shipments"
      orders
      multiple
      hideHeader
      v-model="selected"
      :tableLoader="tableLoader"
    >

      <template slot="thead">
        <shipblu-th>{{$t('Created On')}}</shipblu-th>
        <shipblu-th>{{$t('Tracking No.')}}</shipblu-th>
        <shipblu-th>{{$t('Merchant Name')}}</shipblu-th>
        <shipblu-th>{{$t('Customer Name')}}</shipblu-th>
        <shipblu-th>{{$t('Customer Address')}}</shipblu-th>
        <shipblu-th>{{$t('Packages')}}</shipblu-th>
        <shipblu-th>{{$t('Package Size')}}</shipblu-th>
        <shipblu-th>{{$t('Pickup Date')}}</shipblu-th>
        <shipblu-th>{{$t('Status')}}</shipblu-th>
        <shipblu-th>{{$t('Scheduled Delivery Date')}}</shipblu-th>
        <shipblu-th>{{$t('Delivered On')}}</shipblu-th>
        <shipblu-th v-if="$store.state.AppActiveUser.userRole === 'commercial-admin'" class="order-1 flex justify-center">
          <feather-icon icon="ChevronDownIcon" svgClasses="h-5 w-5"/>
        </shipblu-th>
      </template>

      <template slot-scope="{ data }">
        <shipblu-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data" :color="`${getStyle(data[indextr])}`" :to="{ query: { ...$route.query, order: data[indextr].id, tracking_number: data[indextr].tracking_number } }" viewInPanel @click="viewOrder(data[indextr], true)">
          <shipblu-td :data="data[indextr].createdOn">
            {{ common.splitYearOfDate(data[indextr].created) }}
          </shipblu-td>

          <shipblu-td :data="data[indextr].trackingNumber" class="inactive-link pointer-events-auto">
            <p v-on:click.stop class="flex items-center gap-1">
              <span class="sm:order-2 order-1">{{ data[indextr].tracking_number }}</span>
              <shipblu-icon @click.stop.prevent="common.copied(data[indextr].tracking_number)" iconLibrary="material" iconClass="text-grey hover:text-primary text-xl cursor-pointer sm:order-1 order-2" iconName="content_copy"></shipblu-icon>
            </p>
          </shipblu-td>

          <shipblu-td :data="data[indextr].merchant">
            {{ data[indextr].merchant.name }} {{ data[indextr].merchant.display_name ? '- ' + data[indextr].merchant.display_name : '' }}
          </shipblu-td>

          <shipblu-td :data="data[indextr].customerInfo">
            {{ data[indextr].customer.full_name }}
          </shipblu-td>

          <shipblu-td :data="data[indextr].addressLine">
            {{ data[indextr].customer.address.line_1 }} / {{ data[indextr].customer.address.line_2 }}
          </shipblu-td>

          <shipblu-td :data="data[indextr].packages">
            {{ data[indextr].packages ? data[indextr].packages.length : 0}}
          </shipblu-td>

          <shipblu-td :data="data[indextr].packages">
            <template v-if="data[indextr].packages">
              <p v-for="packageItem in data[indextr].packages" :key="packageItem.index">
                {{ packageItem?.package?.package_size?.short_code || '' }}
              </p>
            </template>
          </shipblu-td>

          <shipblu-td :data="data[indextr].pickupDate">
            {{ data[indextr].pickup_date ? new Date(data[indextr].pickup_date).toLocaleDateString('fr-CA') : ''}}
          </shipblu-td>

          <shipblu-td :data="data[indextr].status" :style="`color: ${getStyle(data[indextr])}`" style="white-space: nowrap;">
            <div class="m-auto flex items-center font-semibold" @click="removeAttribute($event), openNotificationFailureData(data[indextr])">
              <div><span class="status-icon mr-1"></span></div>
              <span class="capitalize">{{ $t(getStatusLabel(data[indextr])) }}</span>
            </div>
          </shipblu-td>
          
          <shipblu-td :data="data[indextr].scheduledDeliveryDate">
            {{ data[indextr].estimated_date}}
          </shipblu-td>
          
          <shipblu-td :data="data[indextr].deliveredOn">
            {{ data[indextr].delivered_date && data[indextr].status === 'delivered' ? new Date(data[indextr].delivered_date).toLocaleDateString('fr-CA') : '' }}
          </shipblu-td>

          <shipblu-td  v-if="$store.state.AppActiveUser.userRole === 'commercial-admin'" class="order-2" style="vertical-align: middle;">
            <div class="md:text-center text-right" @click.stop.prevent="removeAttribute($event)">
              <vs-dropdown
                vs-trigger-click
                @click="selected = []"
                class="dd-actions cursor-pointer"
              >
                <span class="material-symbols-outlined text-2xl">more_horiz</span>
                <vs-dropdown-menu style="width: 190px">
                  <vs-dropdown-item
                    @click="blockCustomer(data[indextr])"
                  >
                    <span class="flex items-center">
                      <feather-icon
                        icon="UserXIcon"
                        svgClasses="h-4 w-4"
                        class="mr-2"
                      />
                      <span>{{$t('Block Customer')}}</span>
                    </span>
                  </vs-dropdown-item>
                </vs-dropdown-menu>
              </vs-dropdown>
            </div>
          </shipblu-td>
        </shipblu-tr>
      </template>
    </shipblu-table>
    <print type="Delivery" :selected="selected.filter(item => item.tracking_number[0] === '4')"></print>
    <block-customer :blockCustomerModal="blockCustomerModal" @blockCustomerModal="blockCustomerModal = $event" :data="customer" @loadShipments="handleSearch($route.query.search)"></block-customer>
    <order-view-panel :orderDataInfo="orderDataInfo" :showPanel="showPanel" @orderData="orderData = $event"></order-view-panel>
  </div>
</template>

<script>
import common  from '@/assets/utils/common'
import {sendRequest} from '../../http/axios/requestHelper.js'
import Print from '../merchant/components/PrintLabels.vue'
import BlockCustomer from '../admin/components/BlockCustomer.vue'
import ShipbluTable from '../../layouts/components/NewShipBluTable.vue'
import ShipbluTr from '../../layouts/components/ShipBluTr.vue'
import ShipbluTh from '../../layouts/components/ShipBluTh.vue'
import ShipbluTd from '../../layouts/components/ShipBluTd.vue'
import ExportButton from './components/ExportButton.vue'
import PrintLabelButton from './components/PrintLabelButton.vue'
import OrderViewPanel from '../merchant/components/OrderViewPanel.vue'

export default {
  data () {
    return {
      localStorage,
      common,
      selected: [],
      shipments: [],
      maximumItems: process.env.VUE_APP_MAXIMUM_ITEMS_TABLE,
      tableLoader: false,
      searchVal: this.$route.query.search || '',
      showPanel: false,
      orderDataInfo: {},
      blockCustomerModal: false,
      customer: {},
      orderData: {}
    }
  },
  watch: {
    'searchVal' (val) {
      if (val === '') {
        this.emptyFields()
      }
    },
    '$route.query.order' (val) {
      if (val) {
        this.showPanel = true
      } else {
        this.shipments = (Array.isArray(this.shipments) ? this.shipments : []).map(order => {
          return order.id === this.orderData.id ? { number_of_packages: this.orderData.number_of_packages, ...order, ...this.orderData } : order
        })
        this.showPanel = false
      }
    },
    '$route.query.search' () {
      if (this.$route.query.search === this.searchVal) {
        return
      }
      this.searchVal = this.$route.query.search || ''
      if (this.$route.query.order) {
        this.showPanel = true
      }
      this.handleSearch(this.$route.query.search)
    }
  },
  methods: {
    viewOrder (data) {
      this.orderDataInfo = data
      this.$router.push({
        query: {
          ...this.$route.query,
          order: data.id
        }
      }).catch(() => {})
    },
    blockCustomer (data) {
      this.customer = data
      this.blockCustomerModal = true
    },
    removeAttribute (event) {
      event.preventDefault()
    },
    openNotificationFailureData (order) {
      common.openNotificationFailureData(this, order)
    },
    getStyle (data) {
      return common.getOrderStatusColor(data.status)
    },
    getOrderStatusColor (order) {
      return common.getOrderStatusColor(this.getStatusLabel(order))
    },
    getStatusLabel (order) {
      return common.getStatusLabel(order, this.$store.state.AppActiveUser.userRole)
    },
    handleSearch (search) {
      if (!search) {
        return
      }
      this.searchVal = search
      this.shipments = []

      const orders = this.getSearchOrders(search)
      const shipmentOrders = orders.filter(order => order[0] === '1' || order.startsWith('31'))
      const returnOrders = orders.filter(order => order[0] === '2' || order.startsWith('32'))
      const cashCollectionOrders = orders.filter(order => order[0] === '4')

      if (shipmentOrders.length || returnOrders.length || cashCollectionOrders.length) {
        this.loadSearchResults(shipmentOrders, returnOrders, cashCollectionOrders)
      } else {
        this.loadShipments('all')
      }

      if (this.$route.query.search !== search) {
        this.$router.push({
          query: {
            ...this.$route.query,
            search
          }
        }).catch(() => {})
      }
    },
    getSearchOrders (search) {
      return `${search}`.split(/[\s,،]+/).map(order => order.trim()).filter(Boolean)
    },
    loadSearchResults (shipmentOrders, returnOrders, cashCollectionOrders) {
      const requests = []

      if (shipmentOrders.length) {
        requests.push(this.fetchSearchResults('api/v1/delivery-orders/', shipmentOrders))
      }

      if (returnOrders.length) {
        requests.push(this.fetchSearchResults('api/v1/returns/', returnOrders))
      }

      if (cashCollectionOrders.length) {
        requests.push(this.fetchSearchResults('api/v1/cash-collections/', cashCollectionOrders))
      }

      this.tableLoader = true
      this.selected = []
      Promise.all(requests).then((results) => {
        this.shipments = results.reduce((orders, result) => [...orders, ...result], [])
        this.tableLoader = false
      }).catch(() => {
        this.tableLoader = false
      })
    },
    fetchSearchResults (endpoint, orders) {
      return new Promise((resolve) => {
        sendRequest(true, false, this, `${endpoint}?search=${orders.join(',')}`, 'get', null, true,
          (response) => {
            resolve(response.data.results)
          },
          () => {
            resolve([])
          }
        )
      })
    },
    loadShipments (type) {
      this.tableLoader = true
      sendRequest(true, false, this, `api/v1/delivery-orders/?search=${this.searchVal}`, 'get', null, true, 
        (response) => {
          this.selected = []
          this.shipments = response.data.results
          if (type) {
            this.loadReturns(type)
          } else {
            this.tableLoader = false
          }
        }
      )
    },
    loadReturns (type) {
      this.tableLoader = true
      sendRequest(true, false, this, `api/v1/returns/?search=${this.searchVal}`, 'get', null, true, 
        (response) => {
          this.selected = []
          const shipments = Array.isArray(this.shipments) ? this.shipments : []
          const results = Array.isArray(response.data.results) ? response.data.results : []
          this.shipments = [...shipments, ...results]
          if (type) {
            this.loadCashCollections()
          } else {
            this.tableLoader = false
          }
        }
      )
    },
    loadCashCollections () {
      this.tableLoader = true
      sendRequest(true, false, this, `api/v1/cash-collections/?search=${this.searchVal}`, 'get', null, true, 
        (response) => {
          this.selected = []
          const shipments = Array.isArray(this.shipments) ? this.shipments : []
          const results = Array.isArray(response.data.results) ? response.data.results : []
          this.shipments = [...shipments, ...results]
          this.tableLoader = false
        }
      )
    },
    emptyFields () {
      this.selected = []
      this.shipments = []
      this.searchVal = ''
      this.$router.push({ query: {} }).catch(() => {})
    }
  },
  mounted () {
    this.$emit('setAppClasses', 'shipment-page')
  },
  components: {
    Print,
    ShipbluTable,
    ShipbluTr,
    ShipbluTh,
    ShipbluTd,
    ExportButton,
    PrintLabelButton,
    OrderViewPanel,
    BlockCustomer
  },
  created () {
    this.showPanel = !!this.$route.query.order
    if (this.$route.query.search) {
      this.searchVal = this.$route.query.search || ''
      this.handleSearch(this.$route.query.search)
    }
  }
}
</script>

<style scoped>
.vs-dialog {
  max-width: 530px;
}
</style>
<style lang="scss">
@import "@/assets/scss/vuexy/apps/email.scss";
#order-label-print{
  display: none;
  visibility: hidden;
}
.con-pagination-table{
  display: none;
}
.vs-input--input {
  padding: 0px !important;
}
</style>
